<script lang="ts" setup>
import { ref } from 'vue';
import { Card, Icon } from '@skillstown/leap';
import { NavigationGroup } from '~~/data/models/NavigationItem';
import { useUser } from '~~/stores/user';

interface FooterLink {
  name: string;
  to: string;
  permissions?: string;
}

const props = defineProps<{
  mainLinks: NavigationGroup[];
  footerLinks?: FooterLink[];
}>();

const user = useUser();
const isSidebarOpen = ref(false);

function closeSidebar(): void {
  isSidebarOpen.value = false;
}

function toggleSidebar(): void {
  isSidebarOpen.value = !isSidebarOpen.value;
}
</script>

<template>
  <div class="layout">
    <header class="layout__header">
      <div class="header__wrapper">
        <div class="header__left">
          <button type="button" class="menu-toggle" @click="toggleSidebar">
            <Icon icon="text" />
          </button>

          <NuxtLink to="/" class="logo hidden lg:block">
            <img src="@/assets/images/nibe-logo.svg" width="80" />
          </NuxtLink>
        </div>

        <NavigationUser />
      </div>
    </header>

    <div class="content_wrapper">
      <div class="layout__content flex min-h-full flex-col">
        <div class="w-full flex-auto justify-center">
          <main class="content__slot">
            <slot></slot>
          </main>
        </div>
        <div class="flex w-full">
          <div class="content__footer">
            <Card class="footer__wrapper" padding="xl">
              <NuxtLink v-for="(link, index) in props.footerLinks" :key="`footer-link-${index}`" :to="link.to">
                {{ link.name }}
              </NuxtLink>
            </Card>
          </div>
        </div>
      </div>

      <div v-if="user.id" :class="[
        'layout__navigation-backdrop',
        isSidebarOpen ? 'layout__navigation-backdrop--visible' : 'layout__navigation-backdrop--hidden',
      ]" @click="closeSidebar"></div>

      <aside v-if="user.id" :class="[
        'layout__navigation',
        isSidebarOpen ? 'layout__navigation--open' : 'layout__navigation--closed',
      ]">
        <NavigationSidebar :main="props.mainLinks" />
      </aside>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.layout {
  @apply h-screen w-screen;
  @apply max-h-screen max-w-[100vw];
  @apply min-h-screen min-w-[100vw];

  .layout__header {
    @apply w-full;
    @apply h-[77px];
    @apply flex items-center justify-center;
    @apply bg-white;
    @apply border-b border-gray-300 border-opacity-70;
    @apply rounded-b-[16px];

    .header__wrapper {
      @apply flex w-full max-w-container-xxl justify-between p-2 px-6;
    }

    .header__left {
      @apply flex items-center gap-3;
    }

    .menu-toggle {
      @apply inline-flex items-center justify-center;
      @apply h-10 w-10 rounded-full border border-gray-300;
    }

    @media (min-width: 1024px) {
      .menu-toggle {
        display: none;
      }
    }
  }

  .content_wrapper {
    @apply lg:flex lg:flex-row-reverse w-full lg:max-w-container-xxl place-self-center;
    height: var(--content-height-without-header);
  }

  .layout__content {
    @apply w-full overflow-hidden;
    height: var(--content-height-without-footer);

    @media (min-width: 1024px) {
      @apply h-full;
    }

    @apply overflow-y-auto overflow-x-hidden;
    @apply bg-gray-50;

    .content__slot {
      @apply min-h-full;
      @apply p-4;
      @apply py-6;
      @apply w-full;
    }

    .content__footer {
      @apply p-4;
      @apply py-6;
      @apply w-full;

      .footer__wrapper {
        @apply flex w-full justify-end p-2 px-6;

        a {
          @apply pl-12;
          color: var(--color-grey-pure);
          font-size: var(--font-size-xxs);
        }
      }
    }
  }

  .layout__navigation {
    @apply border-t border-gray-300 border-opacity-70;
    @apply transition-transform duration-200 ease-out;
    @apply overflow-hidden;

    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 75vw;
    z-index: 50;
    transform: translateX(-100%);

    @media (min-width: 1024px) {
      padding: 1.5rem;
    }
  }

  .layout__navigation--open {
    transform: translateX(0);
  }

  .layout__navigation--closed {
    transform: translateX(-100%);
  }

  .layout__navigation-backdrop {
    position: fixed;
    inset: 0;
    z-index: 40;
    @apply bg-black bg-opacity-40;
    @apply transition-opacity duration-200 ease-out;
  }

  .layout__navigation-backdrop--visible {
    @apply opacity-100 pointer-events-auto;
  }

  .layout__navigation-backdrop--hidden {
    @apply opacity-0 pointer-events-none;
  }

  @media (min-width: 1024px) {
    .layout__navigation {
      position: static;
      @apply h-full max-w-xs w-full;
      @apply border-none shadow-none rounded-none;
      transform: none !important;
      z-index: auto;
    }

    .layout__navigation--open,
    .layout__navigation--closed {
      transform: none !important;
    }

    .layout__navigation-backdrop {
      display: none;
    }
  }
}
</style>
